import { DocumentIntelligence } from "@shared-database/models/document-intelligence";
import { File } from "@shared-database/models/file";
import { User } from "@shared-database/models/user";
import { TermsAcceptance } from "@shared-database/models/terms-acceptance";
import { relations } from "drizzle-orm";
import { FileChecksum } from "@shared-database/models/file-checksum";
import { ScaledImage } from "@shared-database/models/scaled-image";
import { FeatureFlagUser } from "@shared-database/models/feature-flag-user";
import { PreviewFile } from "@shared-database/schema";
import { WorkerJob } from "@shared-database/models/worker-job";
import { DocumentChunk } from "@shared-database/models/rag/document-chunk";
import { DocumentChunkEntity } from "@shared-database/models/rag/document-chunk-entity";
import { KnowledgeGraph } from "@shared-database/models/rag/knowledge-graph";
import { KnowledgeEntity } from "@shared-database/models/rag/knowledge-entity";
import { KnowledgeEntitySource } from "@shared-database/models/rag/knowledge-entity-source";
import { KnowledgeRelation } from "@shared-database/models/rag/knowledge-relation";
import { KnowledgeCommunity } from "@shared-database/models/rag/knowledge-community";
import { KnowledgeCommunityMember } from "@shared-database/models/rag/knowledge-community-member";
import { KnowledgeCommunityFinding } from "@shared-database/models/rag/knowledge-community-finding";

// The file relations
export const FileRelations = relations(File, ({ one, many }) => ({
  owner: one(User, {
    fields: [File.ownerId],
    references: [User.id],
  }),
  docInt: many(DocumentIntelligence),
  scaledImages: many(ScaledImage),
  workerJobs: many(WorkerJob),
  checksum: one(FileChecksum, {
    fields: [File.id],
    references: [FileChecksum.id],
  }),
  preview: one(PreviewFile, {
    fields: [File.id],
    references: [PreviewFile.parentFileId],
  }),
}));

// The file checksum relations
export const FileChecksumRelations = relations(FileChecksum, ({ one }) => ({
  file: one(File, {
    fields: [FileChecksum.id],
    references: [File.id],
  }),
}));

// The file compressed relations
export const ScaledImageRelations = relations(ScaledImage, ({ one }) => ({
  compressed: one(File, {
    fields: [ScaledImage.fileId],
    references: [File.id],
  }),
}));

// the Document intelligence relations
export const DocumentIntelligenceRelations = relations(
  DocumentIntelligence,
  ({ one }) => ({
    file: one(File, {
      fields: [DocumentIntelligence.fileId],
      references: [File.id],
    }),
  }),
);

// The user relations
export const UserRelations = relations(User, ({ many }) => ({
  files: many(File),
  features: many(FeatureFlagUser),
  workerJobs: many(WorkerJob),
}));

export const WorkerJobRelations = relations(WorkerJob, ({ one }) => ({
  file: one(File, {
    fields: [WorkerJob.fileId],
    references: [File.id],
  }),
  user: one(User, {
    fields: [WorkerJob.userId],
    references: [User.id],
  }),
}));

// User feature flag relations
export const FeatureFlagUserRelations = relations(
  FeatureFlagUser,
  ({ one }) => ({
    user: one(User, {
      fields: [FeatureFlagUser.userId],
      references: [User.id],
    }),
  }),
);

// The terms acceptance relations
export const TermsAcceptanceRelations = relations(
  TermsAcceptance,
  ({ one }) => ({
    user: one(User, {
      fields: [TermsAcceptance.ownerId],
      references: [User.id],
    }),
  }),
);

// The preview file relations
export const PreviewFileRelations = relations(PreviewFile, ({ one }) => ({
  file: one(File, {
    fields: [PreviewFile.fileId],
    references: [File.id],
  }),
  parentFile: one(File, {
    fields: [PreviewFile.parentFileId],
    references: [File.id],
  }),
}));

// Document chunk relations
export const DocumentChunkRelations = relations(
  DocumentChunk,
  ({ one, many }) => ({
    file: one(File, {
      fields: [DocumentChunk.fileId],
      references: [File.id],
    }),
    documentIntelligence: one(DocumentIntelligence, {
      fields: [DocumentChunk.documentIntelligenceId],
      references: [DocumentIntelligence.id],
    }),
    chunkEntities: many(DocumentChunkEntity),
  }),
);

// Knowledge graph relations
export const KnowledgeGraphRelations = relations(
  KnowledgeGraph,
  ({ many }) => ({
    entities: many(KnowledgeEntity),
    entitySources: many(KnowledgeEntitySource),
    relations: many(KnowledgeRelation),
    communities: many(KnowledgeCommunity),
  }),
);

// Knowledge entity relations
export const KnowledgeEntityRelations = relations(
  KnowledgeEntity,
  ({ one, many }) => ({
    knowledgeGraph: one(KnowledgeGraph, {
      fields: [KnowledgeEntity.knowledgeGraphId],
      references: [KnowledgeGraph.id],
    }),
    sources: many(KnowledgeEntitySource),
    outgoingRelations: many(KnowledgeRelation, {
      relationName: "sourceEntity",
    }),
    incomingRelations: many(KnowledgeRelation, {
      relationName: "targetEntity",
    }),
    communityMemberships: many(KnowledgeCommunityMember),
    chunkMentions: many(DocumentChunkEntity),
  }),
);

// Knowledge entity source relations
export const KnowledgeEntitySourceRelations = relations(
  KnowledgeEntitySource,
  ({ one }) => ({
    knowledgeGraph: one(KnowledgeGraph, {
      fields: [KnowledgeEntitySource.knowledgeGraphId],
      references: [KnowledgeGraph.id],
    }),
    entity: one(KnowledgeEntity, {
      fields: [KnowledgeEntitySource.entityId],
      references: [KnowledgeEntity.id],
    }),
    file: one(File, {
      fields: [KnowledgeEntitySource.fileId],
      references: [File.id],
    }),
  }),
);

// Knowledge relation relations
export const KnowledgeRelationRelations = relations(
  KnowledgeRelation,
  ({ one }) => ({
    knowledgeGraph: one(KnowledgeGraph, {
      fields: [KnowledgeRelation.knowledgeGraphId],
      references: [KnowledgeGraph.id],
    }),
    sourceEntity: one(KnowledgeEntity, {
      fields: [KnowledgeRelation.sourceEntityId],
      references: [KnowledgeEntity.id],
      relationName: "sourceEntity",
    }),
    targetEntity: one(KnowledgeEntity, {
      fields: [KnowledgeRelation.targetEntityId],
      references: [KnowledgeEntity.id],
      relationName: "targetEntity",
    }),
    sourceFile: one(File, {
      fields: [KnowledgeRelation.sourceFileId],
      references: [File.id],
    }),
  }),
);

// Knowledge community relations
export const KnowledgeCommunityRelations = relations(
  KnowledgeCommunity,
  ({ one, many }) => ({
    knowledgeGraph: one(KnowledgeGraph, {
      fields: [KnowledgeCommunity.knowledgeGraphId],
      references: [KnowledgeGraph.id],
    }),
    members: many(KnowledgeCommunityMember),
    findings: many(KnowledgeCommunityFinding),
  }),
);

// Knowledge community finding relations
export const KnowledgeCommunityFindingRelations = relations(
  KnowledgeCommunityFinding,
  ({ one }) => ({
    community: one(KnowledgeCommunity, {
      fields: [KnowledgeCommunityFinding.communityId],
      references: [KnowledgeCommunity.id],
    }),
  }),
);

// Knowledge community member relations
export const KnowledgeCommunityMemberRelations = relations(
  KnowledgeCommunityMember,
  ({ one }) => ({
    community: one(KnowledgeCommunity, {
      fields: [KnowledgeCommunityMember.communityId],
      references: [KnowledgeCommunity.id],
    }),
    entity: one(KnowledgeEntity, {
      fields: [KnowledgeCommunityMember.entityId],
      references: [KnowledgeEntity.id],
    }),
  }),
);

// Document chunk entity relations
export const DocumentChunkEntityRelations = relations(
  DocumentChunkEntity,
  ({ one }) => ({
    chunk: one(DocumentChunk, {
      fields: [DocumentChunkEntity.chunkId],
      references: [DocumentChunk.id],
    }),
    entity: one(KnowledgeEntity, {
      fields: [DocumentChunkEntity.entityId],
      references: [KnowledgeEntity.id],
    }),
  }),
);
